import React from 'react';
import type { RefObject } from 'react';
import EditableContent from '../EditableContent.tsx';

interface ProductsIntroSectionProps {
  isEditMode: boolean;
  getContent: (key: string) => string;
  onContentSave: (contentId: string, value: string, contentType: 'text' | 'image') => Promise<void> | void;
  onDirtyStateChange: (contentId: string, isDirty: boolean) => void;
  // Dock target for the screw animation
  visualRef: RefObject<HTMLDivElement | null>;
}

const ProductsIntroSection: React.FC<ProductsIntroSectionProps> = ({
  isEditMode,
  getContent,
  onContentSave,
  onDirtyStateChange,
  visualRef,
}) => {
  const editable = (
    contentId: string,
    tag: 'h1' | 'h2' | 'h3' | 'h4' | 'p' | 'span' | 'li' | 'dt' | 'dd' | 'small' | 'strong' | 'em',
    className?: string,
    multiline?: boolean
  ) => (
    <EditableContent
      contentId={contentId}
      contentType="text"
      tag={tag}
      className={className}
      multiline={multiline}
      defaultValue={getContent(contentId)}
      isEditing={isEditMode}
      onSave={onContentSave}
      onDirtyStateChange={onDirtyStateChange}
    />
  );

  return (
    <section id="products" className="products">
      <div className="section-head reveal">
        {editable('products-section-eyebrow', 'p', 'eyebrow')}
        {editable('products-section-tagline', 'span')}
      </div>
      <div className="product-heading reveal">
        <h2>
          {editable('products-heading-line1', 'span')}
          <br />
          <span>{editable('products-heading-accent', 'span')}</span>
        </h2>
        <ul className="product-benefits" aria-label="SKD61 螺絲三項優點">
          {editable('benefit-1', 'li')}
          {editable('benefit-2', 'li')}
          {editable('benefit-3', 'li')}
        </ul>
      </div>
      <div className="product-layout">
        <div
          className="product-visual reveal"
          ref={visualRef}
          role="img"
          aria-label="同一顆刀片鎖固螺絲的金屬外觀與邊緣微紅示意"
        >
          {editable('products-visual-label', 'span', 'visual-label')}
          {editable('products-visual-note', 'span', 'visual-note')}
        </div>
        <div className="specs">
          <article className="spec reveal">
            <span className="spec-num">01</span>
            <div>
              {editable('spec-1-title', 'h3')}
              {editable('spec-1-subtitle', 'p', 'english')}
              {editable('spec-1-description', 'p')}
              {isEditMode ? (
                <>
                  {editable('products-button-text', 'span')}
                  {editable('products-link', 'span')}
                </>
              ) : (
                <a className="button light service-cta" href={getContent('products-link')}>
                  {getContent('products-button-text')} <span aria-hidden="true">↗</span>
                </a>
              )}
            </div>
          </article>
          <article className="spec reveal">
            <span className="spec-num">02</span>
            <div>
              {editable('spec-2-title', 'h3')}
              {editable('spec-2-subtitle', 'p', 'english')}
              {editable('spec-2-description', 'p')}
              {isEditMode ? (
                editable('spec-2-button-text', 'span')
              ) : (
                <a className="button service-cta custom-cta" href="#ordering">
                  {getContent('spec-2-button-text')} <span aria-hidden="true">↓</span>
                </a>
              )}
            </div>
          </article>
        </div>
      </div>
      <div className="material-band reveal">
        {editable('material-band-en', 'span')}
        <strong>
          {editable('material-band-zh', 'span')}
          <em>{editable('material-band-zh-accent', 'span')}</em>
        </strong>
      </div>
    </section>
  );
};

export default ProductsIntroSection;
